import { useQueries, type UseQueryOptions } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import type {
  StorefrontProduct,
  StorefrontProductSummary,
} from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import { useAddToCart } from '@/features/storefront/hooks/useCart';
import type {
  LookbookHotspotBlock,
  LookbookSection,
} from '@/features/storefront/sections/schema';
import { shownBlocks } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * The lookbook: one styled photo - a room, an outfit - with the shop's
 * products tagged on it.
 *
 * Each hotspot is a button at a point given in percent of the picture, so it
 * stays on the sofa it was put on at every width. Tapping one opens a small
 * card with the product's picture, name, price and a link to it - and, for a
 * product sold in one form only, an add-to-cart button. Escape or a tap
 * elsewhere closes it, and focus goes back to the hotspot.
 *
 * Products are looked up by the slug the merchant picked. The full product
 * is fetched where the page can (`productDetailQuery`), because only it knows
 * whether there is a single variant to add; elsewhere the catalog summaries
 * answer, and the card links only.
 */

/** What a hotspot's card shows of its product. */
export interface LookbookProduct {
  slug: string;
  name: string;
  price: string;
  compareAtPrice: string | null;
  imageUrl: string | null;
  /** The one variant a quick add would put in the cart; null for a product with choices. */
  variantId: string | null;
  inStock: boolean;
}

/** How the lookbook asks the shop for one product, by slug. */
export type ProductDetailQueryFactory = (
  slug: string,
) => Pick<UseQueryOptions<StorefrontProduct>, 'queryKey' | 'queryFn'>;

export function lookbookProductFromDetail(product: StorefrontProduct): LookbookProduct {
  const variants = product.variants;
  const single = variants.length === 1 ? variants[0] : null;
  return {
    slug: product.slug,
    name: product.name,
    price: product.price,
    compareAtPrice: product.compare_at_price,
    imageUrl: product.images[0]?.url ?? null,
    variantId: single && single.in_stock ? single.id : null,
    inStock: variants.length === 0 ? true : variants.some((variant) => variant.in_stock),
  };
}

export function lookbookProductFromSummary(product: StorefrontProductSummary): LookbookProduct {
  return {
    slug: product.slug,
    name: product.name,
    price: product.price,
    compareAtPrice: product.compare_at_price,
    imageUrl: product.image_url,
    variantId: null,
    inStock: product.in_stock,
  };
}

/** The slugs a lookbook points at, once each. */
export function lookbookSlugs(section: LookbookSection): string[] {
  return [
    ...new Set(
      section.blocks
        .filter((block) => block.visible)
        .map((block) => block.product.trim())
        .filter(Boolean),
    ),
  ];
}

export interface LookbookViewProps {
  section: LookbookSection;
  /** The customiser: hidden and product-less hotspots are drawn, and nothing is added to a cart. */
  preview: boolean;
  includeHidden?: boolean;
  /** Each hotspot's product by slug; missing while it loads, or when it no longer exists. */
  products: Record<string, LookbookProduct>;
  productHref: (slug: string) => string;
  /** The section's heading, drawn by the renderer so it can be typed into. */
  heading?: ReactNode;
  /** A link the way the renderer draws one: plain text in the customiser. */
  link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => ReactNode;
  /** Wraps a hotspot, so the customiser can select the block. */
  wrap?: (block: LookbookHotspotBlock, rendered: ReactNode) => ReactNode;
  /** Puts one variant in the cart. Unset: the card links only. */
  onAdd?: (variantId: string) => void;
  adding?: boolean;
  added?: boolean;
  /** Shown in the customiser for a lookbook with no picture yet. */
  emptyHint?: ReactNode;
}

const HOTSPOT_BASE =
  'flex items-center justify-center rounded-full font-semibold shadow-md ring-2 ring-white/90 transition-transform hover:scale-110 focus-visible:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

const HOTSPOT_SIZE: Record<LookbookSection['hotspot_style'], string> = {
  dot: 'size-5',
  plus: 'size-8',
  number: 'size-8 text-sm',
};

export function LookbookView({
  section,
  preview,
  includeHidden = false,
  products,
  productHref,
  heading,
  link,
  wrap,
  onAdd,
  adding = false,
  added = false,
  emptyHint,
}: LookbookViewProps) {
  const copy = useShopContent();
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});
  const baseId = useId();

  // Off the shop, a hotspot with no product points at nothing; in the
  // customiser it stays, so the merchant can see where it stands.
  const hotspots = shownBlocks(section.blocks, includeHidden).filter(
    (block) => preview || (block.product.trim() && products[block.product.trim()]),
  );

  useEffect(() => {
    if (open === null) return;
    const close = (event: PointerEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) {
        setOpen(null);
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  if (!section.image_url) {
    return preview ? <>{emptyHint ?? null}</> : null;
  }

  const closeAndFocus = (id: string) => {
    setOpen(null);
    buttons.current[id]?.focus();
  };

  const nameOf = (block: LookbookHotspotBlock) => {
    const product = products[block.product.trim()];
    return block.label.trim() || product?.name || block.product.trim();
  };

  const picture = (
    <div ref={root} className="relative">
      <div className="relative aspect-[4/3] w-full overflow-hidden" style={{ borderRadius: 'var(--storefront-radius)' }}>
        <StorefrontImage
          url={section.image_url}
          sizes="(min-width: 1024px) 66vw, 100vw"
          alt={section.title || ''}
          className="absolute inset-0 size-full object-cover"
          style={focalStyle(section.image_focal)}
        />
      </div>
      {hotspots.map((block) => {
        const index = section.blocks.indexOf(block);
        const product = products[block.product.trim()];
        const name = nameOf(block);
        const isOpen = open === block.id;
        const popoverId = `${baseId}-${block.id}`;
        const button = (
          <button
            ref={(element) => {
              buttons.current[block.id] = element;
            }}
            type="button"
            data-hotspot={block.id}
            aria-expanded={isOpen}
            aria-controls={isOpen ? popoverId : undefined}
            aria-label={copy('section.lookbook_hotspot', { name: name || String(index + 1) })}
            onClick={() => setOpen(isOpen ? null : block.id)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && isOpen) {
                event.preventDefault();
                closeAndFocus(block.id);
              }
            }}
            className={cn(
              HOTSPOT_BASE,
              HOTSPOT_SIZE[section.hotspot_style],
              !block.visible || !product ? 'opacity-60' : undefined,
            )}
            style={{ backgroundColor: 'var(--storefront-primary)', color: 'var(--storefront-background)' }}
          >
            {section.hotspot_style === 'plus' ? (
              <Plus className="size-4" aria-hidden />
            ) : section.hotspot_style === 'number' ? (
              <span aria-hidden>{index + 1}</span>
            ) : null}
          </button>
        );
        // The card opens away from the nearest edges, so it stays on the picture.
        const right = block.x > 60;
        const below = block.y < 50;
        return (
          <div
            key={block.id}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${block.x}%`, top: `${block.y}%` }}
          >
            {wrap ? wrap(block, button) : button}
            {isOpen ? (
              <div
                id={popoverId}
                role="dialog"
                aria-label={name}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    closeAndFocus(block.id);
                  }
                }}
                className={cn(
                  'absolute z-20 w-56 p-3 text-start shadow-xl',
                  right ? 'right-1/2' : 'left-1/2',
                  below ? 'top-full mt-2' : 'bottom-full mb-2',
                )}
                style={{
                  backgroundColor: 'var(--storefront-background)',
                  color: 'var(--storefront-text)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                <HotspotCard
                  name={name}
                  product={product}
                  href={product ? productHref(product.slug) : ''}
                  link={link}
                  onAdd={!preview ? onAdd : undefined}
                  adding={adding}
                  added={added}
                  onClose={() => closeAndFocus(block.id)}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const list =
    section.layout === 'image_with_list' ? (
      <ol className="space-y-3" data-lookbook-list>
        {hotspots.map((block) => {
          const product = products[block.product.trim()];
          const index = section.blocks.indexOf(block);
          if (!product) return null;
          return (
            <li key={block.id}>
              {link(
                productHref(product.slug),
                'flex items-center gap-3',
                <>
                  <span
                    className="flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                    style={{ backgroundColor: 'var(--storefront-primary)', color: 'var(--storefront-background)' }}
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  {product.imageUrl ? (
                    <StorefrontImage
                      url={product.imageUrl}
                      sizes="64px"
                      alt=""
                      className="size-14 shrink-0 object-cover"
                      style={{ borderRadius: 'var(--storefront-radius)' }}
                    />
                  ) : null}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{nameOf(block)}</span>
                    <span className="block text-sm" style={{ color: 'var(--storefront-muted)' }}>
                      {formatMoney(product.price)}
                    </span>
                  </span>
                </>,
              )}
            </li>
          );
        })}
      </ol>
    ) : null;

  return (
    <div data-lookbook-layout={section.layout}>
      {heading}
      {list ? (
        <div className="grid items-start gap-8 lg:grid-cols-[2fr_1fr]">
          {picture}
          {list}
        </div>
      ) : (
        picture
      )}
    </div>
  );
}

function HotspotCard({
  name,
  product,
  href,
  link,
  onAdd,
  adding,
  added,
  onClose,
}: {
  name: string;
  product: LookbookProduct | undefined;
  href: string;
  link: LookbookViewProps['link'];
  onAdd?: (variantId: string) => void;
  adding: boolean;
  added: boolean;
  onClose: () => void;
}) {
  const copy = useShopContent();
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClose}
        aria-label={copy('section.lookbook_close')}
        className="absolute -end-1 -top-1 rounded-full px-1.5 text-lg leading-none opacity-70 hover:opacity-100"
      >
        <span aria-hidden>×</span>
      </button>
      {product?.imageUrl ? (
        <StorefrontImage
          url={product.imageUrl}
          sizes="224px"
          alt=""
          className="mb-2 aspect-square w-full object-cover"
          style={{ borderRadius: 'var(--storefront-radius)' }}
        />
      ) : null}
      <p className="pe-5 text-sm font-semibold">{name}</p>
      {product ? (
        <p className="mt-0.5 text-sm">
          <span>{formatMoney(product.price)}</span>
          {product.compareAtPrice ? (
            <span className="ms-2 line-through" style={{ color: 'var(--storefront-muted)' }}>
              {formatMoney(product.compareAtPrice)}
            </span>
          ) : null}
        </p>
      ) : null}
      {product ? (
        <div className="mt-3 flex flex-col gap-2">
          {onAdd && product.variantId ? (
            <button
              type="button"
              disabled={adding}
              onClick={() => product.variantId && onAdd(product.variantId)}
              className="h-9 w-full px-3 text-xs font-semibold disabled:opacity-60"
              style={STOREFRONT_BUTTON_STYLE}
            >
              {adding
                ? copy('product.adding')
                : added
                  ? copy('account.added_to_cart')
                  : copy('account.add_to_cart')}
            </button>
          ) : null}
          {link(
            href,
            'text-center text-xs font-semibold underline underline-offset-4',
            copy('section.lookbook_view'),
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The lookbook with its products fetched in full, one request per product
 * (cached across the page), and a quick add for a single-variant product.
 */
export function FetchedLookbook({
  query,
  quickAdd,
  ...props
}: Omit<LookbookViewProps, 'products' | 'onAdd' | 'adding' | 'added'> & {
  query: ProductDetailQueryFactory;
  /** Whether the shop has a cart to add to. */
  quickAdd: boolean;
}) {
  const slugs = lookbookSlugs(props.section);
  const results = useQueries({
    queries: slugs.map((slug) => ({ ...query(slug), retry: false, staleTime: 60_000 })),
  });
  const add = useAddToCart();
  const products: Record<string, LookbookProduct> = {};
  slugs.forEach((slug, index) => {
    const product = results[index]?.data;
    if (product) products[slug] = lookbookProductFromDetail(product);
  });

  return (
    <LookbookView
      {...props}
      products={products}
      onAdd={quickAdd ? (variantId) => add.mutate({ variantId }) : undefined}
      adding={add.isPending}
      added={add.isSuccess}
    />
  );
}
